import { lazy, Suspense, type CSSProperties, type ReactNode } from 'react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { AccessNotConfiguredView } from './AccessNotConfiguredView';
import { WorkforceAdmissionDeniedView } from './WorkforceAdmissionDeniedView';
import { AccessErrorView } from './AccessErrorView';
import { SessionExpiredView } from './SessionExpiredView';
import { SigningInView } from './SigningInView';
import { RoutedAppShell } from './shell/RoutedAppShell';

// The legacy `AppShell` is the instant-rollback shell used only when
// `VITE_UCP_ROUTER_SHELL` is off. Lazy-load it so its eager surface imports
// (ComposeSurface, TemplateAdminConsole, AdminConsole, ApprovalSurfaces,
// SchoolSurfaces → CrossSchoolOpsView) do not land in the initial bundle for the
// default routed-shell path.
const AppShell = lazy(() => import('./AppShell').then((m) => ({ default: m.AppShell })));

/**
 * Single `<main>` landmark for the non-routed access states and the legacy shell
 * (the routed shell owns its own `<main>`). Keeps exactly one `main` on every
 * branch now that `App` no longer wraps the tree (PRO-0250, NF-10).
 */
const frameStyle: CSSProperties = {
  fontFamily: 'system-ui, sans-serif',
  maxWidth: 640,
  margin: '4rem auto',
  padding: '0 1rem',
};
function GateFrame({ children }: { children: ReactNode }) {
  return <main style={frameStyle}>{children}</main>;
}

/**
 * Post-authentication gate (PRO-0010): after MSAL sign-in, call `/me` and route
 * the user to the shell, the "access not configured" view, or an accessible
 * error/loading state. Only the configured-user branch chooses the shell; this
 * keeps no-role users out of the router entirely (PRO-0250, D2).
 *
 * The shell choice is feature-flagged (PRO-0250, D8): `VITE_UCP_ROUTER_SHELL`
 * truthy → the routed task-based shell; falsy/unset → the legacy `AppShell`
 * (the instant rollback). `'false'`/`'0'`/empty are treated as off.
 */
function routerShellEnabled(raw: string | undefined): boolean {
  return raw !== undefined && raw !== '' && raw !== '0' && raw.toLowerCase() !== 'false';
}
export function AccessGate({
  onSignOut,
  onReauthenticate,
}: {
  onSignOut: () => void;
  onReauthenticate: () => void;
}) {
  const state = useCurrentUser();

  if (state.status === 'loading' || state.status === 'unauthenticated') {
    return (
      <GateFrame>
        <SigningInView />
      </GateFrame>
    );
  }

  if (state.status === 'expired') {
    // SessionExpiredView owns its own `<main>` (mirrors LandingView), so it is not
    // wrapped in GateFrame — keeps exactly one `main` on this branch (NF-10).
    return <SessionExpiredView reason={state.reason} onReauthenticate={onReauthenticate} />;
  }

  if (state.status === 'error') {
    return (
      <GateFrame>
        <AccessErrorView message={state.message} onSignOut={onSignOut} />
      </GateFrame>
    );
  }

  if (state.user.workforceAdmissionDenied) {
    return (
      <GateFrame>
        <WorkforceAdmissionDeniedView
          reason={state.user.workforceAdmissionDenied}
          onSignOut={onSignOut}
        />
      </GateFrame>
    );
  }

  if (!state.user.accessConfigured) {
    return (
      <GateFrame>
        <AccessNotConfiguredView
          onSignOut={onSignOut}
          accessRequestEnabled={state.user.featureFlags.accessRequestEnabled}
          insightsEnabled={state.user.featureFlags.insightsEnabled}
        />
      </GateFrame>
    );
  }

  if (routerShellEnabled(import.meta.env.VITE_UCP_ROUTER_SHELL)) {
    return <RoutedAppShell user={state.user} onSignOut={onSignOut} />;
  }

  return (
    <GateFrame>
      <Suspense fallback={<SigningInView />}>
        <AppShell user={state.user} onSignOut={onSignOut} />
      </Suspense>
    </GateFrame>
  );
}
